import { endOfDay, startOfDay, subDays } from 'date-fns';
import { createFileRoute } from '@tanstack/react-router';
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import LogoBlueBlack from '@/images/logspot-blue-black.svg?react';
import LogoBlueWhite from '@/images/logspot-blue-white.svg?react';
import {
  isShareCard,
  type ShareCard,
  type ShareTheme,
} from '@logspot-platform/platform-types';
import { EventWidget } from '@logspot-platform/widgets';
import {
  ShareOverview,
  shareColors,
  type ShareCardData,
  type ShareTile,
} from '@/components/dashboard/share-overview';
import { BASE_URL, ApiError } from '@/api/api-client';
import { isReadable } from '@ctrl/tinycolor';

const apiOrigin = BASE_URL.replace(/\/api$/, '');

type DashboardData = {
  name: string;
  config: {
    whitelist: string[];
    description: string;
    primaryColor: string;
    textColor: string;
    chartColor?: string;
    theme?: ShareTheme;
    hidden?: string[];
    cards?: string[];
  };
  eventCounts: Record<string, { name: string; date: string; count: number }[]>;
  dashboardV2: boolean;
  users?: { total: number; perDay: { date: string; count: number }[] };
  breakdowns?: Partial<Record<ShareCard, ShareCardData['rows']>>;
  previous?: { users?: number; events: Record<string, number> };
};

const PAGEVIEW = 'Pageview';

const sum = (rows: { count: number | string }[] = []) =>
  rows.reduce((total, row) => total + Number(row.count), 0);

type RawEventCount = { name: string; date: string; count: number };

const openDashboardOptions = (dashboardId: string) =>
  queryOptions({
    queryKey: ['open-dashboard', dashboardId],
    queryFn: async (): Promise<DashboardData> => {
      const response = await fetch(
        `${apiOrigin}/open-dashboard/${dashboardId}`,
      );
      if (!response.ok) {
        throw new ApiError(
          response.status,
          `Failed to load dashboard (${response.status})`,
        );
      }
      const { data: responseData } = (await response.json()) as {
        data: {
          name: string;
          config: DashboardData['config'];
          eventCounts: RawEventCount[];
          dashboardV2?: boolean;
          users?: DashboardData['users'];
          breakdowns?: DashboardData['breakdowns'];
          previous?: DashboardData['previous'];
        };
      };

      const eventCounts = responseData.eventCounts.reduce(
        (acc: DashboardData['eventCounts'], entry) => {
          const dayMetrics = {
            name: entry.name,
            date: entry.date,
            count: entry.count,
          };
          acc[entry.name] = acc[entry.name]
            ? [...acc[entry.name]!, dayMetrics]
            : [dayMetrics];
          return acc;
        },
        {},
      );

      return {
        name: responseData.name,
        config: responseData.config,
        eventCounts,
        dashboardV2: responseData.dashboardV2 ?? false,
        users: responseData.users,
        breakdowns: responseData.breakdowns,
        previous: responseData.previous,
      };
    },
  });

export const Route = createFileRoute('/shares/$dashboardId')({
  component: OpenDashboard,
  loader: async ({ context, params }) => {
    await context.queryClient.prefetchQuery(
      openDashboardOptions(params.dashboardId),
    );
  },
});

function OpenDashboard() {
  const { dashboardId } = Route.useParams();
  const { data } = useSuspenseQuery(openDashboardOptions(dashboardId));

  const now = endOfDay(new Date());
  const dateRange = {
    interval: '1 days',
    startDate: startOfDay(subDays(now, 30)),
    endDate: now,
  };

  const { eventCounts, name, config } = data;
  const { whitelist, description, primaryColor, textColor, chartColor } =
    config;
  const effectiveChartColor = chartColor ?? textColor;
  const colors = data.dashboardV2
    ? shareColors(config)
    : {
        background: primaryColor,
        foreground: textColor,
        accent: effectiveChartColor,
      };
  const hidden = config.hidden ?? [];
  const tiles: ShareTile[] = [
    ...(data.users
      ? [
          {
            id: 'users',
            label: 'Unique Users',
            total: data.users.total,
            series: data.users.perDay,
            previous: data.previous?.users,
          },
        ]
      : []),
    ...(hidden.includes('pageviews')
      ? []
      : [
          {
            id: 'pageviews',
            label: 'Pageviews',
            total: sum(eventCounts[PAGEVIEW]),
            series: eventCounts[PAGEVIEW] ?? [],
            previous: data.previous && (data.previous.events[PAGEVIEW] ?? 0),
          },
        ]),
    ...whitelist
      .filter((event) => event !== PAGEVIEW)
      .map((event) => ({
        id: `event:${event}`,
        label: event,
        total: sum(eventCounts[event]),
        series: eventCounts[event] ?? [],
        previous: data.previous && (data.previous.events[event] ?? 0),
      })),
  ];
  const cards: ShareCardData[] = (config.cards ?? [])
    .filter(isShareCard)
    .flatMap((id) => {
      const rows = data.breakdowns?.[id];
      return rows ? [{ id, rows }] : [];
    });

  const darkMode = isReadable('#fff', colors.background, {
    level: 'AA',
    size: 'large',
  });

  // Synchronize document theme with dashboard primary color
  const root = window.document.documentElement;
  if (darkMode) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  const isSingle = whitelist.length === 1;

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ background: colors.background, color: colors.foreground }}
    >
      <header className="mx-auto w-full max-w-5xl px-6 pt-8 pb-6 text-center md:pt-10">
        <h1
          className="text-2xl font-semibold md:text-3xl"
          style={{ color: colors.foreground }}
        >
          {name}
        </h1>
        {description && (
          <p
            className="mt-1 text-sm opacity-60"
            style={{ color: colors.foreground }}
          >
            {description}
          </p>
        )}
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-6 pb-8">
        {data.dashboardV2 ? (
          <ShareOverview
            accent={colors.accent}
            theme={config.theme}
            cards={cards}
            dateRange={dateRange}
            hidden={hidden}
            tiles={tiles}
          />
        ) : (
          <div
            className={
              isSingle
                ? 'mx-auto max-w-md'
                : 'grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6'
            }
          >
            {whitelist.map((eventName: string) => (
              <div key={eventName} className="rounded-lg shadow-xs">
                <EventWidget
                  backgroundColor={primaryColor}
                  data={eventCounts[eventName] ?? []}
                  dateRange={dateRange}
                  name={eventName}
                  primaryColor={effectiveChartColor}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="flex w-full items-center justify-center gap-2 py-5">
        <span className="text-sm font-medium opacity-60">Powered by</span>
        <a
          className="flex items-center"
          href="https://www.logspot.io"
          rel="noopener noreferrer"
          target="_blank"
        >
          {darkMode ? (
            <LogoBlueWhite className="h-5 w-auto opacity-60" />
          ) : (
            <LogoBlueBlack className="h-5 w-auto opacity-60" />
          )}
        </a>
      </footer>
    </div>
  );
}
